import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { LanguageProvider } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ScrollProgress from "@/components/ScrollProgress";
import ScrollScene3D from "@/components/ScrollScene3D";

import Home from "@/pages/Home";
import Gallery from "@/pages/Gallery";
import Menu from "@/pages/Menu";
import Hotels, { HotelDetail } from "@/pages/Hotels";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Blog, { BlogPostPage } from "@/pages/Blog";
import Recipes from "@/pages/Recipes";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

// Umre Acente Paneli (TR only)
import { AcenteAuthProvider, useAcenteAuth } from "@/context/AcenteAuthContext";
import AcenteLayout from "@/pages/acente/AcenteLayout";
import AcenteLogin from "@/pages/acente/AcenteLogin";
import AcenteRegister from "@/pages/acente/AcenteRegister";
import AcenteDashboard from "@/pages/acente/AcenteDashboard";
import AcenteReservations from "@/pages/acente/AcenteReservations";
import AcenteReservationForm from "@/pages/acente/AcenteReservationForm";
import OzelAcenteReservationForm from "@/pages/acente/OzelAcenteReservationForm";
import AcenteTrash from "@/pages/acente/AcenteTrash";
import AcenteOpsPricing from "@/pages/acente/AcenteOpsPricing";
import AcenteDeletedAgencies from "@/pages/acente/AcenteDeletedAgencies";
import AcenteReservationDetail from "@/pages/acente/AcenteReservationDetail";
import AcenteBulkImport from "@/pages/acente/AcenteBulkImport";
import AcenteAnalytics from "@/pages/acente/AcenteAnalytics";
import AcenteMonthlyReport from "@/pages/acente/AcenteMonthlyReport";
import AcenteCollectedPayments from "@/pages/acente/AcenteCollectedPayments";
import SahaHome from "@/pages/saha/SahaHome";
import AcenteInvoiceRequests from "@/pages/acente/AcenteInvoiceRequests";
import AcenteTodayCheckins from "@/pages/acente/AcenteTodayCheckins";
import AcenteReservationTracking from "@/pages/acente/AcenteReservationTracking";
import AcenteReservationTrackingOperasyon from "@/pages/acente/AcenteReservationTrackingOperasyon";
import AcenteSettings from "@/pages/acente/AcenteSettings";
import AcentePasswordResetRequests from "@/pages/acente/AcentePasswordResetRequests";
import AcentePlanningHub from "@/pages/acente/AcentePlanningHub";
import AcenteHotels from "@/pages/acente/AcenteHotels";
import AcenteGroupProgram from "@/pages/acente/AcenteGroupProgram";
import AcenteRoomList from "@/pages/acente/AcenteRoomList";
import AcenteAgencyDetail from "@/pages/acente/AcenteAgencyDetail";
import {
  AcenteMealPlanning,
  AcenteRoomPlanning,
  AcenteReports,
  AcenteAgencies,
  AcenteUsers,
  AcenteLogs,
  AcenteRevisions,
} from "@/pages/acente/AcentePages";
import { Navigate } from "react-router-dom";

function PublicLayout({ children }) {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <ScrollScene3D />
      <main className="relative min-h-[60vh]">{children}</main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

/** Requires an authenticated panel session; otherwise sends to /acente/giris. */
function AcenteGuard() {
  const { user, loading } = useAcenteAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/acente/giris" replace />;
  // Saha çalışanı gets a completely different, mobile-first UI at /saha —
  // hard-redirect them so they never see the admin/acente layout.
  if (user.role === "saha_calisani") return <Navigate to="/saha" replace />;
  return <AcenteLayout />;
}

/** Same auth check as AcenteGuard but only lets saha_calisani through. */
function SahaGuard() {
  const { user, loading } = useAcenteAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/acente/giris" replace />;
  if (user.role !== "saha_calisani") return <Navigate to="/acente" replace />;
  return <SahaHome />;
}

/** Dispatches to the correct reservation form based on the logged-in user's
 *  role. Özel acente (B2C) gets a single-page rapid form; everyone else gets
 *  the 5-step wizard. */
function ReservationFormRouter() {
  const { user } = useAcenteAuth();
  if (user?.role === "ozel_acente") return <OzelAcenteReservationForm />;
  return <AcenteReservationForm />;
}

/** Panel index route ("/acente"). Operasyon's job is pricing review — dropping
 *  them straight into that queue makes their first click productive. Every
 *  other role sees the normal dashboard. */
function AcenteIndexRouter() {
  const { user } = useAcenteAuth();
  if (user?.role === "operasyon") return <Navigate to="/acente/fiyat-onaylari" replace />;
  return <AcenteDashboard />;
}

/** Guards `/acente/rezervasyonlar` — operasyon should not be here (their
 *  workflow is the pricing approval queue + the simplified takip view).
 *  Redirect them to the tracking page. */
function ReservationsGuard() {
  const { user } = useAcenteAuth();
  if (user?.role === "operasyon") return <Navigate to="/acente/takip" replace />;
  return <AcenteReservations />;
}

/** Dispatches the takip (tracking) page — operasyon gets the simplified
 *  one-line-per-row view with an "İnceleme Talep Et" button; admin keeps
 *  the full Excel grid with every column. */
function TrackingRouter() {
  const { user } = useAcenteAuth();
  if (user?.role === "operasyon") return <AcenteReservationTrackingOperasyon />;
  return <AcenteReservationTracking />;
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <LanguageProvider>
          <AuthProvider>
            <AcenteAuthProvider>
              <Toaster position="top-center" richColors />
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
                  <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
                  <Route path="/menu" element={<PublicLayout><Menu /></PublicLayout>} />
                  <Route path="/hotels" element={<PublicLayout><Hotels /></PublicLayout>} />
                  <Route path="/hotels/:id" element={<PublicLayout><HotelDetail /></PublicLayout>} />
                  <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
                  <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
                  <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
                  <Route path="/recipes" element={<PublicLayout><Recipes /></PublicLayout>} />
                  <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
                  <Route path="/admin/login" element={<PublicLayout><AdminLogin /></PublicLayout>} />
                  <Route path="/admin" element={<PublicLayout><AdminDashboard /></PublicLayout>} />

                  {/* Umre Acente Paneli — Türkçe */}
                  <Route path="/acente/giris" element={<AcenteLogin />} />
                  <Route path="/acente/kayit" element={<AcenteRegister />} />
                  <Route path="/saha" element={<SahaGuard />} />
                  <Route path="/acente" element={<AcenteGuard />}>
                    <Route index element={<AcenteIndexRouter />} />
                    <Route path="rezervasyonlar" element={<ReservationsGuard />} />
                    <Route path="silinen-kayitlar" element={<AcenteTrash />} />
                    <Route path="fiyat-onaylari" element={<AcenteOpsPricing />} />
                    <Route path="silinen-sirketler" element={<AcenteDeletedAgencies />} />
                    <Route path="rezervasyon/yeni" element={<ReservationFormRouter />} />
                    <Route path="rezervasyon/:id" element={<AcenteReservationDetail />} />
                    <Route path="rezervasyon/:id/duzenle" element={<ReservationFormRouter />} />
                    <Route path="toplu-yukle" element={<AcenteBulkImport />} />
                    <Route path="bugun-girisler" element={<AcenteTodayCheckins />} />
                    <Route path="takip" element={<TrackingRouter />} />
                    <Route path="fatura-talepleri" element={<AcenteInvoiceRequests />} />
                    <Route path="ayarlar" element={<AcenteSettings />} />
                    <Route path="sifre-talepleri" element={<AcentePasswordResetRequests />} />
                    <Route path="planlama-raporlar" element={<AcentePlanningHub />} />
                    <Route path="yemek-planlama" element={<AcenteMealPlanning />} />
                    <Route path="oda-planlama" element={<AcenteRoomPlanning />} />
                    <Route path="raporlar" element={<AcenteReports />} />
                    <Route path="analitik" element={<AcenteAnalytics />} />
                    <Route path="aylik-rapor" element={<AcenteMonthlyReport />} />
                    <Route path="toplanan-ucretler" element={<AcenteCollectedPayments />} />
                    <Route path="revizyon-talepleri" element={<AcenteRevisions />} />
                    <Route path="acenteler" element={<AcenteAgencies />} />
                    <Route path="acenteler/:id" element={<AcenteAgencyDetail />} />
                    <Route path="oteller" element={<AcenteHotels />} />
                    <Route path="grup-programi" element={<AcenteGroupProgram />} />
                    <Route path="oda-listesi" element={<AcenteRoomList />} />
                    <Route path="kullanicilar" element={<AcenteUsers />} />
                    <Route path="loglar" element={<AcenteLogs />} />
                  </Route>
                </Routes>
              </BrowserRouter>
            </AcenteAuthProvider>
          </AuthProvider>
        </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}
